// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Reservations Screen
// Search + filter tabs + reservation list + detail actions
// ---------------------------------------------------------------------------

import { useState, useMemo, useCallback } from 'react';
import SearchBar from '@/components/SearchBar';
import ReservationList from '@/components/ReservationList';
import { useReservationStore } from '@/store/reservation-store';
import { useCartStore } from '@/store/cart-store';
import { useAppStore } from '@/store/app-store';
import { useReservations } from '@/hooks/useReservations';
import { apiClient } from '@/services/api-client';
import type { Reservation } from '@/lib/types';

type FilterTab = 'all' | 'today' | 'active';

export default function ReservationsScreen() {
  useReservations();

  const reservations = useReservationStore((s) => s.reservations);
  const searchReservation = useReservationStore((s) => s.search);
  const getTodayReservations = useReservationStore((s) => s.getTodayReservations);
  const getActiveReservations = useReservationStore((s) => s.getActiveReservations);
  const showToast = useAppStore((s) => s.showToast);
  const setScreen = useAppStore((s) => s.setScreen);
  const setScannerOpen = useAppStore((s) => s.setScannerOpen);
  const setTable = useCartStore((s) => s.setTable);
  const setCartReservation = useCartStore((s) => s.setReservation);

  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [selectedResa, setSelectedResa] = useState<Reservation | null>(null);

  // Compute filtered results
  const filteredReservations = useMemo(() => {
    // Search takes priority
    if (query.trim()) {
      return searchReservation(query);
    }

    switch (activeFilter) {
      case 'today':
        return getTodayReservations();
      case 'active':
        return getActiveReservations();
      default:
        return [...reservations].sort((a, b) => {
          const da = new Date(`${a.date}T${a.time || '00:00'}`);
          const db = new Date(`${b.date}T${b.time || '00:00'}`);
          return db.getTime() - da.getTime();
        });
    }
  }, [query, activeFilter, reservations, searchReservation, getTodayReservations, getActiveReservations]);

  // Stats
  const stats = useMemo(
    () => ({
      total: reservations.length,
      today: getTodayReservations().length,
      active: getActiveReservations().length,
    }),
    [reservations, getTodayReservations, getActiveReservations],
  );

  const handleSelect = useCallback((resa: Reservation) => {
    setSelectedResa(resa);
  }, []);

  // ---- Detail actions ----

  const handleUseInCart = () => {
    if (!selectedResa) return;
    if (selectedResa.table_id) {
      setTable(selectedResa.table_id);
    }
    setCartReservation(selectedResa.id);
    showToast('Reservation associee au panier', 'success');
    setSelectedResa(null);
    setScreen('caisse');
  };

  const handleRelease = async () => {
    if (!selectedResa) return;
    try {
      await apiClient.deleteReservation(selectedResa.id);
      showToast('Reservation liberee', 'success');
      setSelectedResa(null);
    } catch {
      showToast('Erreur liberation reservation', 'error');
    }
  };

  const handleScan = () => {
    setScannerOpen(true);
  };

  const FILTERS: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'Toutes' },
    { key: 'today', label: "Aujourd'hui" },
    { key: 'active', label: 'Actives' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Search + scan */}
      <div className="px-4 py-2 shrink-0 flex items-center gap-2">
        <div className="flex-1">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Rechercher par nom, telephone, QR..."
          />
        </div>
        <button
          type="button"
          onClick={handleScan}
          className="btn-secondary p-2.5 rounded-xl shrink-0"
          aria-label="Scanner QR"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 6V1h-5" />
            <path d="M1 6V1h5" />
            <path d="M23 18v5h-5" />
            <path d="M1 18v5h5" />
            <line x1="1" y1="12" x2="23" y2="12" />
          </svg>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 px-4 py-2 shrink-0">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => {
              setActiveFilter(f.key);
              setQuery('');
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              activeFilter === f.key && !query.trim()
                ? 'bg-accent text-white'
                : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 px-4 py-2 shrink-0">
        {[
          { label: 'Total', value: stats.total },
          { label: "Aujourd'hui", value: stats.today },
          { label: 'Actives', value: stats.active },
        ].map((s) => (
          <div key={s.label} className="bg-surface-card rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-accent">{s.value}</p>
            <p className="text-[11px] text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Reservation list */}
      <ReservationList
        reservations={filteredReservations}
        onSelect={handleSelect}
      />

      {/* Detail modal (bottom sheet style) */}
      {selectedResa && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-black/60 animate-fade-in">
          <div className="bg-surface rounded-t-2xl md:rounded-xl w-full max-w-md max-h-[80vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">
                Details reservation
              </h3>
              <button
                type="button"
                onClick={() => setSelectedResa(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3 text-sm">
              {[
                { label: 'ID', value: selectedResa.id },
                { label: 'Client', value: selectedResa.client_name || 'Non renseigne' },
                { label: 'Telephone', value: selectedResa.client_phone || 'Non renseigne' },
                { label: 'Table', value: selectedResa.table_id || 'Non assignee' },
                { label: 'Personnes', value: String(selectedResa.party_size) },
                { label: 'Statut', value: selectedResa.status },
              ].map((row) => (
                <div key={row.label} className="flex justify-between py-1.5 border-b border-white/5">
                  <span className="text-gray-400">{row.label}</span>
                  <span className="text-white font-medium">{row.value}</span>
                </div>
              ))}
              {selectedResa.notes && (
                <div className="pt-1">
                  <span className="text-gray-400 text-xs">Notes:</span>
                  <p className="text-white text-sm mt-1">{selectedResa.notes}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 px-4 py-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSelectedResa(null)}
                className="btn-secondary flex-1 text-xs py-2"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={handleRelease}
                className="btn-danger flex-1 text-xs py-2"
              >
                Liberer
              </button>
              <button
                type="button"
                onClick={handleUseInCart}
                className="btn-primary flex-1 text-xs py-2"
              >
                Utiliser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
